import {
  base_colorContentBorder,
  base_colorSurfaceSecondary,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_4xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

const paddingHorizontal = spacing(10)

/*
  The design wants the whole flyout chrome on a single, uniform off-white
  tint — header area, body, and the gap above the title all the same
  color. BaseFlyout defaults to a header-to-body linear-gradient on the
  container plus separate CloseBar/Header/Body backgrounds, which paints
  a visible seam. Pin every layer to base_colorSurfaceSecondary so the
  surface reads as continuous.

  We don't pass BaseFlyout's title/subtitle props (the subtitle slot is
  string-only and we need an inline link), so .BaseFlyout-Header doesn't
  render — title + description live inside the body via the `header`
  style below.
*/
export default css`
  &.AssignAccountOwnerFlyout {
    .BaseFlyout-Container:has(.BaseFlyout-Header),
    .BaseFlyout-Container {
      background: ${base_colorSurfaceSecondary};
    }

    .BaseFlyout-Content {
      display: flex;
      flex-direction: column;
      /*
        BaseFlyout-Content defaults to overflow: scroll, which scrolls the
        whole flyout. We want a fixed chrome (header, owner section, footer)
        with only the card list scrolling, so clip here and let the inner
        cardsContainer own the scroll.
      */
      overflow: hidden;

      .BaseFlyout-CloseBar {
        padding: ${spacing(5)} ${paddingHorizontal} 0;
        background-color: ${base_colorSurfaceSecondary};
      }

      .BaseFlyout-Body {
        padding: 0;
        background-color: ${base_colorSurfaceSecondary};
        flex: auto;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
    }
  }
`

/*
  Title + description block. The separator at the bottom is an absolutely
  positioned ::after with horizontal insets so it doesn't run edge-to-
  edge — matching the design's inset divider.
*/
export const header = css`
  position: relative;
  padding: ${spacing(6)} ${paddingHorizontal} ${spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${spacing(3)};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: ${paddingHorizontal};
    right: ${paddingHorizontal};
    height: 1px;
    background-color: ${base_colorContentBorder};
  }
`

export const flyoutTitle = css`
  margin: 0;
  font-family: ${font_family_weight_medium};
  /*
    Rubik-Medium already encodes the weight via the font file (~500).
    The browser's default <h2> { font-weight: bold } would otherwise
    layer a synthetic bold on top — pin font-weight: 500 to stop that.
  */
  font-weight: 500;
  ${font_4xl}
  color: ${text_colorTextPrimary};
`

export const description = css`
  margin: 0;
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextSecondary};
`

export const ownerSection = css`
  padding: ${spacing(5)} ${paddingHorizontal};
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};
`

export const ownerLabel = css`
  ${font_md};
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextPrimary};
`

export const cardsCaption = css`
  padding: ${spacing(2)} ${paddingHorizontal} ${spacing(3)};
  ${font_sm};
  font-family: ${font_family_weight_medium};
  color: ${text_colorTextSecondary};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

export const cardsContainer = css`
  flex: auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 ${paddingHorizontal} ${spacing(5)};
`

/*
  Sticky footer with a top-shadow that floats above the scrolling card
  list. z-index ensures the shadow renders above tbody rows; position:
  relative establishes the stacking context.
*/
export const footer = css`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${base_colorSurfaceSecondary};
  padding: ${spacing(5)} ${paddingHorizontal};
  border-top: 1px solid ${base_colorContentBorder};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
`
